import React, { useCallback, useEffect, useState, useMemo } from "react";
import EditIcon from "@styles/all-images/icons/edit.svg";
import DownloadIcon from "@styles/all-images/icons/download.svg";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import CustomTable from "@/components/common/table/CustomTable";
import dateHandler from "@/composables/dateUtil";
import ListIcon from "@styles/all-images/icons/list-icon.svg";
import UnarchiveIcon from "@styles/all-images/icons/unarchived.svg";
import MasterModal from "./MasterModal";
import { toast } from "react-toastify";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import { EndpointType } from "@/constants/apiEndPoints";

export type MasterType =
  | "commodity"
  | "profession"
  | "debtorType"
  | "paymentTerms"
  | "clientType";

export interface MasterDataTableProps {
  type: MasterType;
  fetchData: (
    type: MasterType,
    params: { limit?: number; page?: number; search?: string },
    isArchiveView: boolean,
  ) => Promise<any>;
}

const MasterDataTable: React.FC<MasterDataTableProps> = ({
  type,
  fetchData,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { toStandardDisplayDate } = dateHandler();
  const [forceRefresh, setForceRefresh] = useState(false);
  const [isArchiveView, setIsArchiveView] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [columns, setColumns] = useState<any>([]);
  const titleNameObject = useMemo(
    () => ({
      commodity: {
        title: "title",
        display: "Commodity",
      },
      profession: {
        title: "title",
        display: "Profession",
      },
      debtorType: {
        title: "debtor_type",
        display: "Debtor Type",
      },
      paymentTerms: {
        title: "payment_term",
        display: "Payment Terms",
      },
      clientType: {
        title: "client_type",
        display: "Client Type",
      },
    }),
    [],
  );

  const typeConfig = {
    commodity: {
      addText: "Add Commodity",
      editPath: "/edit-commodity",
      addPath: "/add-commodity",
    },
    profession: {
      addText: "Add Profession",
      editPath: "/edit-profession",
      addPath: "/add-profession",
    },
    debtorType: {
      addText: "Add Debtor Type",
      editPath: "/edit-debtor-type",
      addPath: "/add-debtor-type",
    },
    paymentTerms: {
      addText: "Add Payment Terms",
      editPath: "/edit-payment-terms",
      addPath: "/add-payment-terms",
    },
    clientType: {
      addText: "Add Client Type",
      editPath: "/edit-client-type",
      addPath: "/add-client-type",
    },
  };

  const handleFetch = useCallback(
    async (params: {
      page: number;
      limit: number;
      search?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
    }) => {
      return fetchData(type, params, isArchiveView);
    },
    [type, isArchiveView, fetchData],
  );

  const handleArchived = useCallback(
    async (row: any, isCurrentlyArchived: boolean) => {
      try {
        const { unAuthenticatedCall: archiveMaster } = actionsHandler(
          (type + "Archive") as EndpointType,
        );

        const payload = {
          is_archive: !isCurrentlyArchived,
        };

        const response = await dispatch(
          archiveMaster({
            payload,
            method: "patch",
            customParams: [row.id + "/"],
          }),
        );

        if (response.ok) {
          toast.success(
            response.data?.message ||
              `${titleNameObject[type].display} ${payload.is_archive ? "archived" : "unarchived"} successfully`,
          );
          setForceRefresh(true);
        } else {
          if (Array.isArray(response.data.message)) {
            response.data.message.forEach((message: string) => {
              toast.error(message);
            });
          } else {
            toast.error(
              response?.data?.message ||
                `Failed to ${payload.is_archive ? "archive" : "unarchive"} item`,
            );
          }
        }
      } catch (error) {
        console.error("Archive error:", error);
        toast.error("Failed to update archive status");
      }
    },
    [type, dispatch, titleNameObject],
  );

  const handleSettingColumns = useCallback(() => {
    setColumns([
      {
        header: titleNameObject[type].display,
        accessor: titleNameObject[type].title,
        sortable: true,
        sortKey: "title",
      },
      {
        header: "Uploaded On",
        accessor: (row: any) =>
          toStandardDisplayDate({
            inputDate: row.created_at,
            inputFormat: "DD-MM-YYYY HH:mm:ss",
            desiredFormat: "dateTime",
          }),
        sortable: true,
        sortKey: "created_at",
      },
      {
        header: "Action",
        accessor: (row: any) => (
          <div className="tw-flex tw-items-center tw-gap-5">
            {!isArchiveView ? (
              <>
                <button
                  onClick={() => {
                    setSelectedItem(row);
                    setModalMode("edit");
                    setModalOpen(true);
                  }}
                >
                  <img
                    src={EditIcon}
                    alt="Edit Icon"
                    className="tw-w-6 tw-h-6"
                  />
                </button>
                <button
                  className="tw-text-blue-500"
                  onClick={() => handleArchived(row, isArchiveView)}
                >
                  <img
                    src={DownloadIcon}
                    alt="Download Icon"
                    className="tw-w-6 tw-h-6"
                  />
                </button>
              </>
            ) : (
              <button
                className="tw-text-blue-500"
                onClick={() => handleArchived(row, isArchiveView)}
              >
                <img
                  src={UnarchiveIcon}
                  alt="Unarchive Icon"
                  className="tw-w-6 tw-h-6"
                />
              </button>
            )}
          </div>
        ),
      },
    ]);
  }, [type, isArchiveView, handleArchived]);

  useEffect(() => {
    setForceRefresh(true);
    handleSettingColumns();
  }, [type, isArchiveView]);

  return (
    <>
      <CustomTable
        columns={columns}
        fetchData={handleFetch}
        addButton={{
          text: typeConfig[type].addText,
          icon: PlusIcon,
          onClick: () => {
            setModalMode("create");
            setSelectedItem(null);
            setModalOpen(true);
          },
        }}
        archiveButton={{
          text: isArchiveView ? "Back to List" : "Archived",
          icon: isArchiveView ? ListIcon : DownloadIcon,
          onClick: () => setIsArchiveView(!isArchiveView),
          isArchiveView,
        }}
        forceRefresh={forceRefresh}
        setForceRefresh={setForceRefresh}
      />

      {modalOpen && (
        <MasterModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          type={type}
          mode={modalMode}
          titleConfig={titleNameObject[type]}
          initialData={selectedItem}
          onSuccess={() => setForceRefresh(true)}
        />
      )}
    </>
  );
};

export default MasterDataTable;
